import { useAtom } from 'jotai';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Select } from '@/components/ui/select';
import { TextArea } from '@/components/ui/text-area';
import { cn } from '@/lib/utils';
import type { WatermarkPosition } from '@/utils/watermark';
import { DEFAULT_WATERMARK_TEXT, watermarkAtom } from '../../atoms/settings';
import { SettingsSectionHeader } from './settings-section-header';
import { Checkbox } from '@/components/ui/checkbox';

export const WatermarkSection = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(true);
  const [watermarkInfo, setWatermarkInfo] = useAtom(watermarkAtom);

  const positionOptions = useMemo(
    () =>
      [
        { id: 'bottom-left', title: t('bottom-left') },
        { id: 'bottom-right', title: t('bottom-right') },
        { id: 'top-left', title: t('top-left') },
        { id: 'top-right', title: t('top-right') },
      ] satisfies { id: WatermarkPosition; title: string }[],
    [t]
  );

  return (
    <div className={cn('w-full rounded-sm border border-slate-200 bg-white sm:w-[287px]')}>
      <SettingsSectionHeader
        label={t('watermark')}
        isOpen={isOpen}
        onClick={() => setIsOpen((current) => !current)}
      />

      {isOpen ? (
        <div className="space-y-3 px-3 pb-3">
          <p className="text-xs text-slate-600">{t('watermark-description')}</p>
          <Select
            label={t('Position')}
            value={watermarkInfo.position}
            size="sm"
            options={positionOptions}
            onChange={(opt) => {
              setWatermarkInfo((prev) => ({
                ...prev,
                position: opt.target.value as WatermarkPosition,
              }));
            }}
          />
          <TextArea
            label={t('watermark-text')}
            value={watermarkInfo.text}
            onChange={(event) =>
              setWatermarkInfo((prev) => ({ ...prev, text: event.target.value }))
            }
            placeholder={DEFAULT_WATERMARK_TEXT}
            color="primary"
            rows={2}
            className="h-[58px] w-full resize-none overflow-y-auto rounded-sm border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-700 outline-none placeholder:text-slate-400 focus-visible:border-primary-light"
            wrapperClassName="overflow-hidden"
          />

          <Checkbox
            label={t('addWatermark')}
            checked={watermarkInfo.applyWatermark}
            className="size-5"
            onCheckedChange={(checked) => {
              setWatermarkInfo((prev) => ({ ...prev, applyWatermark: checked === true }));
            }}
          />
        </div>
      ) : null}
    </div>
  );
};
